import { ApiError, NetworkError } from '../../lib/api';
import type { AuthedRequest } from '../auth/AuthProvider';

/** Mirrors GET /api/v1/platform/tenants (ADR-028). */
export interface TenantSummary {
  id: string;
  slug: string;
  name: string;
  status: TenantStatus;
  timezone: string;
  /** The platform tenant itself: never suspended nor entered. */
  platform: boolean;
  created_at: string;
  /** People of the tenant (super administrators' stand-ins excluded). */
  users: number;
  /** Cameras and recorder channels. */
  cameras: number;
}

export type TenantStatus = 'active' | 'suspended';

export interface CreateTenantBody {
  slug: string;
  name: string;
  timezone: string;
  admin_email: string;
  admin_name: string;
  admin_password: string;
}

export const tenantsKey = ['platform', 'tenants'] as const;

export async function listTenants(req: AuthedRequest): Promise<TenantSummary[]> {
  return (await req<{ items: TenantSummary[] }>('GET', '/api/v1/platform/tenants')).items;
}

/** What a tenant holds; mirrors GET /api/v1/platform/overview. */
export interface Inventory {
  clients: number;
  accounts: number;
  users: number;
  /** Cameras on their own and NVR channels. */
  ip_cameras: number;
  /** DVR channels and encoders. */
  analog_cameras: number;
  dvrs: number;
  nvrs: number;
  /** Enabled cameras last seen offline or in error. */
  cameras_down: number;
  recorders_down: number;
  /** Active integrations. */
  integrations: number;
}

export interface TenantInventory extends Inventory {
  id: string;
  slug: string;
  name: string;
  status: TenantStatus;
}

export interface PlatformOverview {
  /** Every tenant but the platform. */
  totals: Inventory & { tenants: number; tenants_suspended: number };
  items: TenantInventory[];
}

export const overviewKey = ['platform', 'overview'] as const;

export const getOverview = (req: AuthedRequest) =>
  req<PlatformOverview>('GET', '/api/v1/platform/overview');

export function createTenant(req: AuthedRequest, body: CreateTenantBody): Promise<TenantSummary> {
  return req<TenantSummary>('POST', '/api/v1/platform/tenants', body);
}

export function setTenantStatus(
  req: AuthedRequest,
  id: string,
  status: TenantStatus,
): Promise<TenantSummary> {
  return req<TenantSummary>('PATCH', `/api/v1/platform/tenants/${encodeURIComponent(id)}`, {
    status,
  });
}

/** Changes the identifier typed at login; data and sessions are unaffected. */
export function setTenantSlug(
  req: AuthedRequest,
  id: string,
  slug: string,
): Promise<TenantSummary> {
  return req<TenantSummary>('PATCH', `/api/v1/platform/tenants/${encodeURIComponent(id)}`, {
    slug,
  });
}

export const statusLabels: Record<TenantStatus, string> = {
  active: 'Ativa',
  suspended: 'Suspensa',
};

/** Turns a company name into an identifier suggestion (lower-case, hyphens). */
export function suggestSlug(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63)
    .replace(/-+$/, '');
}

const messages: Record<string, Record<string, string>> = {
  slug: {
    invalid: 'Use letras minúsculas, números e hífens (sem começar ou terminar com hífen).',
    taken: 'Já existe uma empresa com este identificador.',
  },
  name: { invalid: 'Informe o nome da empresa (até 200 caracteres).' },
  timezone: { invalid: 'Escolha um fuso horário válido.' },
  admin_email: { invalid: 'Informe um e-mail válido.' },
  admin_name: { invalid: 'Informe o nome do administrador (até 200 caracteres).' },
  admin_password: {
    too_short: 'A senha deve ter pelo menos 12 caracteres.',
    too_long: 'A senha é longa demais.',
  },
};

export interface Problem {
  fields: Record<string, string>;
  form: string | null;
}

export const noProblem: Problem = { fields: {}, form: null };

/** Maps a platform API failure onto form-level and field-level messages. */
export function toProblem(err: unknown): Problem {
  if (err instanceof ApiError) {
    if (err.status === 422) {
      const fields: Record<string, string> = {};
      for (const [field, code] of Object.entries(err.fields)) {
        fields[field] = messages[field]?.[code] ?? 'Valor inválido.';
      }
      return { fields, form: null };
    }
    if (err.code === 'tenant_suspended')
      return { fields: {}, form: 'Esta empresa está suspensa. Reative-a antes de entrar.' };
    if (err.status === 403) return { fields: {}, form: 'Você não tem permissão para esta ação.' };
    if (err.status === 404) return { fields: {}, form: 'Esta empresa não existe mais.' };
  }
  if (err instanceof NetworkError)
    return { fields: {}, form: 'Não foi possível contatar o servidor. Tente novamente.' };
  return { fields: {}, form: 'Ocorreu um erro inesperado. Tente novamente.' };
}
